import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { useSnapshots, useRollback, type SnapshotRow } from "@/hooks/useSnapshots.js";
import { useListing } from "@/hooks/useListing.js";
import { Button } from "@/components/ui/button.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { SnapshotDiff } from "@/components/signature/SnapshotDiff.js";
import { RoleGate } from "@/components/RoleGate.js";
import { ListingChrome } from "@/components/signature/ListingChrome.js";

export default function ListingSnapshots(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (!id) return <p className="text-risk">id mancante</p>;

  const { data: snapshots = [] } = useSnapshots(id);
  const { data: listing } = useListing(id);
  const rollback = useRollback(id);
  const [openDiff, setOpenDiff] = useState<SnapshotRow | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<SnapshotRow | null>(null);

  const published = snapshots[0] ?? null;
  const publishedPayload = (published?.payload ?? null) as Record<string, unknown> | null;

  async function doRollback() {
    if (!rollbackTarget) return;
    try {
      await rollback.mutateAsync(rollbackTarget.id);
      toast.success(`Rollback eseguito a ${rollbackTarget.id.slice(0, 8)}...`);
      setRollbackTarget(null);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      {id ? <ListingChrome id={id} /> : null}
      <header>
        <p className="font-mono text-meta uppercase tracking-wider text-ink-mute">
          <Link to={`/listings/${id}`} className="hover:text-ink">↶ editor</Link>
          {listing ? ` · ${listing.slug}` : ""}
        </p>
        <h1 className="mt-1 text-h1 font-display text-ink">Snapshot</h1>
        <p className="text-body text-ink-soft">{snapshots.length} snapshot · {published ? `pubblicato ${new Date(published.published_at).toLocaleString("it-IT")}` : "mai pubblicato"}</p>
      </header>

      <div className="overflow-x-auto rounded border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[200px]">Pubblicato</TableHeader>
              <TableHeader>Hash</TableHeader>
              <TableHeader className="w-[120px]">Stato</TableHeader>
              <TableHeader className="w-[240px] text-right">Azioni</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshots.map((s, idx) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-meta text-ink">
                  {new Date(s.published_at).toLocaleString("it-IT")}
                </TableCell>
                <TableCell className="font-mono text-meta text-ink-mute">{s.payload_hash.slice(0, 24)}...</TableCell>
                <TableCell className="font-mono text-meta uppercase text-ink-mute">
                  {idx === 0 ? "pubblicato" : s.invalidated_at ? "invalidato" : "storico"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setOpenDiff(s)}>Diff vs pubblicato</Button>
                    <RoleGate min="admin">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRollbackTarget(s)}
                        disabled={idx === 0}
                      >
                        <RotateCcw className="size-3.5" /> Rollback
                      </Button>
                    </RoleGate>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Drawer open={!!openDiff} onOpenChange={(o) => !o && setOpenDiff(null)}>
        <DrawerContent>
          <div className="space-y-4 p-6">
            <DrawerTitle className="text-h2 font-display text-ink">Diff snapshot</DrawerTitle>
            {openDiff ? (
              <SnapshotDiff
                left={publishedPayload}
                right={(openDiff.payload ?? null) as Record<string, unknown> | null}
                leftLabel={`pubblicato · ${published ? published.payload_hash.slice(0, 8) : "—"}`}
                rightLabel={`questo · ${openDiff.payload_hash.slice(0, 8)}`}
              />
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>

      <ConfirmDialog
        open={!!rollbackTarget}
        onOpenChange={(o) => !o && setRollbackTarget(null)}
        title="Eseguire rollback?"
        description={
          rollbackTarget ? (
            <span>
              Lo snapshot pubblico tornerà a quello del{" "}
              <strong>{new Date(rollbackTarget.published_at).toLocaleString("it-IT")}</strong>. La cache CF
              verrà purgata. L'azione è registrata nell'audit log.
            </span>
          ) : null
        }
        confirmLabel="Esegui rollback"
        destructive
        loading={rollback.isPending}
        onConfirm={doRollback}
      />
    </div>
  );
}
